import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Typography,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import { FeatureHeader, ScrollToTopOnMount } from "frame/components";
import { isSuperAdminUser } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";

const CACHE_KEY_PREFIX = "superAdminDashboardUsageCache:v11";

const FEATURE_ORDER = [
	"timesheets",
	"timesheetsOffline",
	"leave",
	"tasks",
	"staff",
	"photosNotes",
	"photosNotesPins",
	"pinnedToSite",
	"pinnedToCustomer",
	"pinnedToStaff",
	"documents",
	"incidents",
	"safetyMinutes",
	"inductionMeetings",
	"jsa",
	"siteSafetyPlans",
	"siteInspectionChecklists",
	"hazardLibrary",
	"hazardousSubstanceLibrary",
	"customers",
	"sites",
	"equipment",
	"timesheetEquipment",
	"equipmentUsage",
];

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	header: {
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(-3),
			marginBottom: theme.spacing(-3),
		},
	},
	card: {
		marginBottom: theme.spacing(2),
	},
	tableHeader: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	row: {
		display: "flex",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		padding: `${theme.spacing(0.75)}px 0`,
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	rowLast: {
		borderBottom: "none",
	},
	meta: {
		color: theme.palette.text.secondary,
	},
	banner: {
		padding: theme.spacing(1.5),
		background: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	adminTools: {
		marginTop: theme.spacing(3),
	},
	adminButtons: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
	},
	resultSummary: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
}));

const adoptionPercent = (feature) => {
	const used = feature?.organisationsWithUsageCount || 0;
	const never = feature?.organisationsNeverUsedCount || 0;
	const total = used + never;
	if (!total) return null;
	return Math.round((used / total) * 100);
};

const money = (value) =>
	`$${(Number(value) || 0).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

const mapStateToProps = ({ profile }) => ({
	role: profile.role,
});

export const SuperAdminDashboard = connect(mapStateToProps)(({ role }) => {
	const classes = useStyles();

	const [state, setState] = useState({
		loading: true,
		data: null,
		error: null,
		fromCache: false,
	});

	const [rangePreset, setRangePreset] = useState(() => {
		try {
			const raw = window.localStorage.getItem(`${CACHE_KEY_PREFIX}:rangePreset`);
			// Migrate away from legacy all_time preset (we no longer support it in the UI).
			if (raw === "all_time") return "last_3_months";
			return raw || "last_3_months";
		} catch (_e) {
			return "last_3_months";
		}
	});

	const [range, setRange] = useState(() => {
		try {
			const raw = window.localStorage.getItem(`${CACHE_KEY_PREFIX}:range`);
			// Default to last 3 months if nothing stored.
			if (!raw) {
				return {
					from: moment().subtract(3, "months").startOf("day"),
					to: moment().startOf("day"),
				};
			}
			const parsed = JSON.parse(raw);
			const storedFrom = parsed?.from ? moment(parsed.from) : null;
			const storedTo = parsed?.to ? moment(parsed.to) : null;

			if (!storedFrom || !storedTo) {
				// If storage exists but was cleared, still default to last 3 months.
				return {
					from: moment().subtract(3, "months").startOf("day"),
					to: moment().startOf("day"),
				};
			}

			return { from: storedFrom.startOf("day"), to: storedTo.startOf("day") };
		} catch (_e) {
			return {
				from: moment().subtract(3, "months").startOf("day"),
				to: moment().startOf("day"),
			};
		}
	});

	const [excludeZ, setExcludeZ] = useState(() => {
		try {
			const raw = window.localStorage.getItem(`${CACHE_KEY_PREFIX}:excludeZ`);
			// Default: exclude z - orgs
			return raw === null ? true : raw === "true";
		} catch (_e) {
			return true;
		}
	});

	const [stripeTools, setStripeTools] = useState({
		loading: false,
		error: null,
		mode: null,
		data: null,
	});

	const canView = useMemo(() => isSuperAdminUser(role), [role]);

	// Keep the default preset and From/To values in sync (esp. when old localStorage has an "all time" range saved).
	useEffect(() => {
		if (!canView) return;

		let storedPreset = null;
		let storedRange = null;

		try {
			storedPreset = window.localStorage.getItem(`${CACHE_KEY_PREFIX}:rangePreset`);
		} catch (_e) {
			// ignore
		}

		try {
			const raw = window.localStorage.getItem(`${CACHE_KEY_PREFIX}:range`);
			storedRange = raw ? JSON.parse(raw) : null;
		} catch (_e) {
			storedRange = null;
		}

		// If there's no preset persisted, default it to last 3 months.
		if (!storedPreset) {
			const to = moment().startOf("day");
			const from = moment().subtract(3, "months").startOf("day");

			setRangePreset("last_3_months");
			setRange({ from, to });

			try {
				window.localStorage.setItem(`${CACHE_KEY_PREFIX}:rangePreset`, "last_3_months");
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:range`,
					JSON.stringify({ from: from.toISOString(), to: to.toISOString() })
				);
			} catch (_e) {
				// ignore
			}
			return;
		}

		// Migrate away from "all_time" preset by forcing last 3 months + matching dates.
		if (storedPreset === "all_time") {
			const to = moment().startOf("day");
			const from = moment().subtract(3, "months").startOf("day");

			setRangePreset("last_3_months");
			setRange({ from, to });

			try {
				window.localStorage.setItem(`${CACHE_KEY_PREFIX}:rangePreset`, "last_3_months");
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:range`,
					JSON.stringify({ from: from.toISOString(), to: to.toISOString() })
				);
			} catch (_e) {
				// ignore
			}
			return;
		}

		// If preset says last 3 months but stored range is missing/cleared, fix it.
		if (storedPreset === "last_3_months" && (!storedRange?.from || !storedRange?.to)) {
			const to = moment().startOf("day");
			const from = moment().subtract(3, "months").startOf("day");

			setRangePreset("last_3_months");
			setRange({ from, to });

			try {
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:range`,
					JSON.stringify({ from: from.toISOString(), to: to.toISOString() })
				);
			} catch (_e) {
				// ignore
			}
		}
	}, [canView]);

	const toRangeParams = useCallback((from, to) => {
		if (!from || !to) return { fromUtc: null, toUtc: null };

		const fromUtc = moment(from).startOf("day").utc().toISOString();
		const toUtc = moment(to).endOf("day").utc().toISOString();
		return { fromUtc, toUtc };
	}, []);

	const getCacheKey = useCallback(
		(fromUtc, toUtc) => {
			const rangeKey = !fromUtc || !toUtc ? "all" : `${fromUtc}:${toUtc}`;
			return `${CACHE_KEY_PREFIX}:${rangeKey}:excludeZ=${excludeZ ? "1" : "0"}`;
		},
		[excludeZ]
	);

	const loadFromCache = useCallback(() => {
		try {
			const { fromUtc, toUtc } = toRangeParams(range.from, range.to);
			const raw = window.localStorage.getItem(getCacheKey(fromUtc, toUtc));
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!parsed || !parsed.data) return null;
			return parsed;
		} catch (_e) {
			return null;
		}
	}, []);

	const fetchDashboard = useCallback(async () => {
		setState((s) => ({ ...s, loading: true, error: null, fromCache: false }));

		try {
			const { fromUtc, toUtc } = toRangeParams(range.from, range.to);
			const qsParts = [];
			if (fromUtc && toUtc) {
				qsParts.push(`fromUtc=${encodeURIComponent(fromUtc)}`);
				qsParts.push(`toUtc=${encodeURIComponent(toUtc)}`);
			}
			qsParts.push(`excludeZ=${excludeZ ? "true" : "false"}`);
			const qs = qsParts.length ? `?${qsParts.join("&")}` : "";

			const result = await safetyWiseApi.get(`super-admin/dashboard/usage${qs}`);
			const data = result.data;

			try {
				window.localStorage.setItem(
					getCacheKey(fromUtc, toUtc),
					JSON.stringify({ cachedAtUtc: new Date().toISOString(), data })
				);
			} catch (_e) {
				// Ignore storage failures (quota, privacy mode, etc.)
			}

			setState({ loading: false, data, error: null, fromCache: false });
		} catch (error) {
			const cached = loadFromCache();
			if (cached?.data) {
				setState({ loading: false, data: cached.data, error, fromCache: true });
			} else {
				setState({ loading: false, data: null, error, fromCache: false });
			}
		}
	}, [excludeZ, getCacheKey, loadFromCache, range.from, range.to, toRangeParams]);

	useEffect(() => {
		if (!canView) {
			setState({ loading: false, data: null, error: null, fromCache: false });
			return;
		}

		fetchDashboard();
	}, [canView, fetchDashboard]);

	const { loading, data, error, fromCache } = state;

	const orderedFeatures = useMemo(() => {
		const features = data?.features || [];
		if (!Array.isArray(features) || features.length === 0) return [];

		const orderIndex = FEATURE_ORDER.reduce((acc, key, idx) => {
			acc[key] = idx;
			return acc;
		}, {});

		return [...features].sort((a, b) => {
			const aKey = a?.featureKey;
			const bKey = b?.featureKey;
			const aIdx = typeof orderIndex[aKey] === "number" ? orderIndex[aKey] : 9999;
			const bIdx = typeof orderIndex[bKey] === "number" ? orderIndex[bKey] : 9999;
			if (aIdx !== bIdx) return aIdx - bIdx;
			return (a?.featureName || "").localeCompare(b?.featureName || "");
		});
	}, [data]);

	const cachedAt = useMemo(() => {
		const cached = loadFromCache();
		if (!cached?.cachedAtUtc) return null;
		try {
			return new Date(cached.cachedAtUtc);
		} catch (_e) {
			return null;
		}
	}, [loadFromCache]);

	const applyQuickRange = useCallback(
		(months) => {
			const to = moment().startOf("day");
			const from = moment().subtract(months, "months").startOf("day");
			setRange({ from, to });
			setRangePreset(months === 1 ? "last_month" : `last_${months}_months`);
			try {
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:range`,
					JSON.stringify({ from: from.toISOString(), to: to.toISOString() })
				);
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:rangePreset`,
					months === 1 ? "last_month" : `last_${months}_months`
				);
			} catch (_e) {
				// ignore
			}
		},
		[setRange]
	);

	const updateRange = useCallback(
		(next) => {
			setRange({
				from: next.from ? moment(next.from).startOf("day") : null,
				to: next.to ? moment(next.to).startOf("day") : null,
			});
			setRangePreset("custom");
			try {
				window.localStorage.setItem(
					`${CACHE_KEY_PREFIX}:range`,
					JSON.stringify({
						from: next.from ? moment(next.from).startOf("day").toISOString() : null,
						to: next.to ? moment(next.to).startOf("day").toISOString() : null,
					})
				);
				window.localStorage.setItem(`${CACHE_KEY_PREFIX}:rangePreset`, "custom");
			} catch (_e) {
				// ignore
			}
		},
		[setRange]
	);

	useEffect(() => {
		try {
			window.localStorage.setItem(`${CACHE_KEY_PREFIX}:excludeZ`, excludeZ ? "true" : "false");
		} catch (_e) {
			// ignore
		}
	}, [excludeZ]);

	const runStripeAudit = useCallback(async () => {
		setStripeTools({ loading: true, error: null, mode: "audit", data: null });
		try {
			const result = await safetyWiseApi.get(
				"organisation/subscription/quantity-audit?estimatedMonths=3"
			);
			setStripeTools({ loading: false, error: null, mode: "audit", data: result.data });
		} catch (error) {
			setStripeTools({
				loading: false,
				error: error?.response?.data || error?.message || "Failed to load refund report.",
				mode: "audit",
				data: null,
			});
		}
	}, []);

	const runStripePreview = useCallback(async () => {
		setStripeTools({ loading: true, error: null, mode: "preview", data: null });
		try {
			const result = await safetyWiseApi.post(
				"organisation/subscription/quantity-repair?dryRun=true"
			);
			setStripeTools({ loading: false, error: null, mode: "preview", data: result.data });
		} catch (error) {
			setStripeTools({
				loading: false,
				error: error?.response?.data || error?.message || "Failed to preview Stripe sync.",
				mode: "preview",
				data: null,
			});
		}
	}, []);

	const runStripeApply = useCallback(async () => {
		const confirmed = window.confirm(
			"This will update Stripe subscription quantities to match active staff for all organisations. It does not issue refunds. Continue?"
		);
		if (!confirmed) return;

		setStripeTools({ loading: true, error: null, mode: "apply", data: null });
		try {
			const result = await safetyWiseApi.post(
				"organisation/subscription/quantity-repair?dryRun=false"
			);
			setStripeTools({ loading: false, error: null, mode: "apply", data: result.data });
		} catch (error) {
			setStripeTools({
				loading: false,
				error: error?.response?.data || error?.message || "Failed to apply Stripe sync.",
				mode: "apply",
				data: null,
			});
		}
	}, []);

	const mismatchRows =
		stripeTools.data?.rows || stripeTools.data?.remainingMismatches || [];
	const syncRows = stripeTools.data?.syncResults || [];

	return (
		<div className={classes.root}>
			<ScrollToTopOnMount />
			<FeatureHeader title="Super Admin Dashboard" className={classes.header} />

			{!canView && (
				<Box className={classes.banner}>
					<Typography variant="body1">
						You don’t have access to this page.
					</Typography>
				</Box>
			)}

			{canView && (
				<>
					<Box className={classes.banner}>
						<Box display="flex" justifyContent="space-between" alignItems="flex-start">
							<Box flex={1} pr={2}>
								<Typography variant="body1">
									Usage snapshot across features (Top 3 customers per feature).
								</Typography>
								<Typography variant="body2" className={classes.meta}>
									Range: {range.from?.format("Do MMM YYYY")} → {range.to?.format("Do MMM YYYY")}
								</Typography>
								{fromCache && (
									<Typography variant="body2" className={classes.meta}>
										Showing cached data{cachedAt ? ` (cached ${cachedAt.toLocaleString()})` : ""}.
									</Typography>
								)}
								{data?.generatedAtUtc && (
									<Typography variant="body2" className={classes.meta}>
										Generated {new Date(data.generatedAtUtc).toLocaleString()}.
									</Typography>
								)}
								{!!error && !fromCache && (
									<Typography variant="body2" color="error">
										Failed to load dashboard data.
									</Typography>
								)}
							</Box>
							<Box display="flex" flexDirection="column" alignItems="flex-end" gridGap={8}>
								<Button
									variant="contained"
									color="primary"
									onClick={fetchDashboard}
									disabled={loading}
								>
									Apply / Refresh
								</Button>
							</Box>
						</Box>

						<Box mt={2}>
							<Box display="flex" flexWrap="wrap" gridGap={8} mb={2}>
								<Button
									size="small"
									variant={rangePreset === "last_month" ? "contained" : "outlined"}
									color={rangePreset === "last_month" ? "primary" : "default"}
									onClick={() => applyQuickRange(1)}
								>
									Last month
								</Button>
								<Button
									size="small"
									variant={rangePreset === "last_3_months" ? "contained" : "outlined"}
									color={rangePreset === "last_3_months" ? "primary" : "default"}
									onClick={() => applyQuickRange(3)}
								>
									Last 3 months
								</Button>
								<Button
									size="small"
									variant={rangePreset === "last_6_months" ? "contained" : "outlined"}
									color={rangePreset === "last_6_months" ? "primary" : "default"}
									onClick={() => applyQuickRange(6)}
								>
									Last 6 months
								</Button>
								<Button
									size="small"
									variant={rangePreset === "last_12_months" ? "contained" : "outlined"}
									color={rangePreset === "last_12_months" ? "primary" : "default"}
									onClick={() => applyQuickRange(12)}
								>
									Last 12 months
								</Button>
								<Button
									size="small"
									variant={rangePreset === "last_24_months" ? "contained" : "outlined"}
									color={rangePreset === "last_24_months" ? "primary" : "default"}
									onClick={() => applyQuickRange(24)}
								>
									Last 2 years
								</Button>
							</Box>

							<Box mb={1}>
								<FormControlLabel
									control={
										<Checkbox
											checked={excludeZ}
											onChange={(e) => setExcludeZ(e.target.checked)}
											color="primary"
										/>
									}
									label='Exclude organisations containing "z - "'
								/>
							</Box>

							<Grid container spacing={2}>
								<Grid item xs={12} sm={6} md={4}>
									<DatePicker
										fullWidth
										autoOk
										inputVariant="outlined"
										variant="outlined"
										label="From"
										format="Do MMM YYYY"
										value={range.from}
										onChange={(value) => updateRange({ ...range, from: value })}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<DatePicker
										fullWidth
										autoOk
										inputVariant="outlined"
										variant="outlined"
										label="To"
										format="Do MMM YYYY"
										value={range.to}
										minDate={range.from || undefined}
										onChange={(value) => updateRange({ ...range, to: value })}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<Box display="flex" alignItems="center" height="100%">
										<Typography variant="body2" className={classes.meta}>
											Note: Customers, Sites, and Pin metrics don’t have timestamps, so they show all-time totals even when a range is selected.
										</Typography>
									</Box>
								</Grid>
							</Grid>
						</Box>
					</Box>

					{loading && (
						<Box display="flex" justifyContent="center" mt={4}>
							<CircularProgress />
						</Box>
					)}

					{!!orderedFeatures.length && (
						<Grid container spacing={2}>
							{orderedFeatures.map((feature) => {
								const pct = adoptionPercent(feature);
								return (
								<Grid item xs={12} key={feature.featureKey}>
									<Card className={classes.card}>
										<CardContent>
											<Typography variant="h6">{feature.featureName}</Typography>
											<Typography variant="body2" className={classes.meta}>
												{feature.isRangeSupported
													? "In selected range: "
													: "All time (range not supported): "}
												Used by {feature.organisationsWithUsageCount} orgs
												{pct !== null ? ` (${pct}%)` : ""}
												{feature.featureKey === "timesheetEquipment"
													? ` • ${(feature.totalUsageCount || 0).toLocaleString()} timesheets`
													: ""}{" "}
												• Never used by {feature.organisationsNeverUsedCount} orgs
												{pct !== null ? ` (${100 - pct}%)` : ""}
											</Typography>
											{feature.featureKey === "timesheetEquipment" && (
												<Typography variant="body2" className={classes.meta}>
													Timesheets that have equipment selected. Usage records are not included.
												</Typography>
											)}

											<Typography
												variant="subtitle2"
												className={classes.tableHeader}
											>
												{feature.featureKey === "timesheetsOffline"
													? "Customers with offline timesheets"
													: feature.featureKey === "timesheetEquipment"
														? "Customers with equipment on timesheets"
														: "Top customers"}
											</Typography>
											<Box>
												{(feature.topCustomers || []).map((row, idx, arr) => (
													<Box
														key={`${feature.featureKey}-top-${row.organisationId}`}
														className={`${classes.row} ${
															idx === arr.length - 1 ? classes.rowLast : ""
														}`}
													>
														<Box>
															<Typography variant="body2">
																{row.organisationName}
															</Typography>
															{row.lastUsedAtUtc && (
																<Typography variant="caption" className={classes.meta}>
																	Last used{" "}
																	{new Date(row.lastUsedAtUtc).toLocaleString()}
																</Typography>
															)}
														</Box>
														<Typography variant="body2">
															{row.usageCount.toLocaleString()}
															{feature.featureKey === "timesheetEquipment" ? " timesheets" : ""}
														</Typography>
													</Box>
												))}
												{!(feature.topCustomers || []).length && (
													<Typography variant="body2" className={classes.meta}>
														{feature.featureKey === "timesheetEquipment"
															? "No timesheets with equipment in this range."
															: "No usage recorded."}
													</Typography>
												)}
											</Box>

											{!!(feature.leastRecentlyUsedCustomers || []).length && (
												<>
													<Typography
														variant="subtitle2"
														className={classes.tableHeader}
													>
														Least recently used (but not zero)
													</Typography>
													<Box>
														{(feature.leastRecentlyUsedCustomers || []).map(
															(row, idx, arr) => (
																<Box
																	key={`${feature.featureKey}-least-${row.organisationId}`}
																	className={`${classes.row} ${
																		idx === arr.length - 1 ? classes.rowLast : ""
																	}`}
																>
																	<Box>
																		<Typography variant="body2">
																			{row.organisationName}
																		</Typography>
																		{row.lastUsedAtUtc && (
																			<Typography
																				variant="caption"
																				className={classes.meta}
																			>
																				Last used{" "}
																				{new Date(row.lastUsedAtUtc).toLocaleString()}
																			</Typography>
																		)}
																	</Box>
																	<Typography variant="body2">
																		{row.usageCount.toLocaleString()}
																		{feature.featureKey === "timesheetEquipment" ? " timesheets" : ""}
																	</Typography>
																</Box>
															)
														)}
													</Box>
												</>
											)}
										</CardContent>
									</Card>
								</Grid>
								);
							})}
						</Grid>
					)}

					<Card className={`${classes.card} ${classes.adminTools}`}>
						<CardContent>
							<Typography variant="h6">Stripe Sync Admin Tools</Typography>
							<Typography variant="body2" className={classes.meta}>
								Use the refund report to see who was billed for inactive staff.
								Preview does not change Stripe. Apply syncs quantities going forward — refunds still happen in the Stripe Dashboard.
							</Typography>

							<Box className={classes.adminButtons}>
								<Button
									variant="contained"
									color="primary"
									onClick={runStripeAudit}
									disabled={stripeTools.loading}
								>
									Refund report
								</Button>
								<Button
									variant="outlined"
									color="primary"
									onClick={runStripePreview}
									disabled={stripeTools.loading}
								>
									Preview sync
								</Button>
								<Button
									variant="outlined"
									color="secondary"
									onClick={runStripeApply}
									disabled={stripeTools.loading}
								>
									Apply Stripe sync
								</Button>
							</Box>

							{stripeTools.loading && (
								<Box display="flex" alignItems="center" gridGap={8}>
									<CircularProgress size={20} />
									<Typography variant="body2" className={classes.meta}>
										Talking to Stripe — this can take a minute.
									</Typography>
								</Box>
							)}

							{!!stripeTools.error && (
								<Typography variant="body2" color="error">
									{typeof stripeTools.error === "string"
										? stripeTools.error
										: "Request failed. Check you are signed in as Super Admin."}
								</Typography>
							)}

							{stripeTools.data && !stripeTools.loading && (
								<Box className={classes.resultSummary}>
									{stripeTools.mode === "audit" && (
										<>
											<Typography variant="subtitle2">
												{stripeTools.data.mismatchCount || 0} org(s) overcharged
											</Typography>
											<Typography variant="body2" className={classes.meta}>
												Seats: {stripeTools.data.totalOverchargedSeats || 0} • Monthly:{" "}
												{money(stripeTools.data.totalMonthlyOverchargeEstimate)} • Est. 3-month refund:{" "}
												{money(stripeTools.data.totalEstimatedRefundForPeriod)} • Invoice-based:{" "}
												{money(stripeTools.data.totalInvoiceBasedOverchargeEstimate)}
											</Typography>
										</>
									)}
									{stripeTools.mode === "preview" && (
										<Typography variant="subtitle2">
											Preview only — {stripeTools.data.mismatchCount || 0} org(s) would be updated. No Stripe changes made.
										</Typography>
									)}
									{stripeTools.mode === "apply" && (
										<Typography variant="subtitle2">
											Synced {stripeTools.data.stripeSyncUpdated || 0} of {stripeTools.data.stripeSyncAttempted || 0} Stripe subscription(s). Role flags updated: {stripeTools.data.organisationUserRolesUpdated || 0}. Remaining mismatches: {(stripeTools.data.remainingMismatches || []).length}.
										</Typography>
									)}
								</Box>
							)}

							{!!mismatchRows.length && (
								<Box mt={1}>
									<Typography variant="subtitle2" className={classes.tableHeader}>
										Overcharged organisations
									</Typography>
									{mismatchRows.map((row, idx, arr) => (
										<Box
											key={row.organisationId}
											className={`${classes.row} ${
												idx === arr.length - 1 ? classes.rowLast : ""
											}`}
										>
											<Box>
												<Typography variant="body2">{row.organisationName}</Typography>
												<Typography variant="caption" className={classes.meta}>
													Active staff {row.correctActiveUsers} • Stripe qty {row.stripeBaseQuantity} • Overcharged seats {row.overchargedSeats}
												</Typography>
											</Box>
											<Typography variant="body2">
												{money(row.estimatedRefundForPeriod || row.monthlyOverchargeEstimate)}
											</Typography>
										</Box>
									))}
								</Box>
							)}

							{!!syncRows.length && (
								<Box mt={2}>
									<Typography variant="subtitle2" className={classes.tableHeader}>
										Stripe sync results
									</Typography>
									{syncRows.map((row, idx, arr) => (
										<Box
											key={row.organisationId}
											className={`${classes.row} ${
												idx === arr.length - 1 ? classes.rowLast : ""
											}`}
										>
											<Box>
												<Typography variant="body2">{row.organisationName}</Typography>
												<Typography variant="caption" className={classes.meta}>
													{row.message || (row.updated ? "Updated" : "Unchanged")}
												</Typography>
											</Box>
											<Typography variant="body2">
												{row.previousBaseQuantity ?? "-"} → {row.updatedBaseQuantity ?? row.correctQuantity}
											</Typography>
										</Box>
									))}
								</Box>
							)}
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
});

